/**
 * Wrap Monaco editor. See also:
 * import MonacoEditor from '@wandb/common/components/Monaco/Editor';
 * This is a controlled component that automatically sets its height based on
 * the text content.
 */

import Editor from '@monaco-editor/react';
import {Colors} from '@wandb/common/css/globals.styles';
import React, {useRef, useState} from 'react';

type CodeEditorProps = {
  value: string;
  language: string;
  readOnly?: boolean;
  onChange?: (value: string) => void;
};

export const CodeEditor = ({
  value,
  language,
  readOnly,
  onChange,
}: CodeEditorProps) => {
  const editorRef = useRef(null);
  const [height, setHeight] = useState(300);

  const updateHeight = () => {
    const editor: any = editorRef.current;
    if (editor) {
      const contentHeight = editor.getContentHeight();
      setHeight(contentHeight);
    }
  };

  const handleEditorBeforeMount = (monaco: any) => {
    monaco.editor.defineTheme('wandb-light', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': Colors.GRAY_100,
        'editorWidget.border': Colors.GRAY_350,
      },
    });
  };

  const handleEditorDidMount = (editor: any, monaco: any) => {
    monaco.editor.setTheme('wandb-light');
    editorRef.current = editor;
    editor.onDidContentSizeChange(updateHeight);
    updateHeight();
  };

  const width = '100%';
  const options = {
    readOnly,
    minimap: {
      enabled: false,
    },
    overviewRulerLanes: 0,
    scrollBeyondLastLine: false,

    // If we are autosizing, don't capture scroll events so we can scroll past editor.
    // Note that this also means we have to explicitly use horizontal scrollbar to view clipped content.
    scrollbar: {
      handleMouseWheel: false,
    },
  };

  const onValueChange = (newValue: string | undefined) => {
    if (onChange) {
      onChange(newValue ?? '');
    }
  };

  return (
    <div
      style={{
        width,
        height: `${height}px`,
        border: `1px solid ${Colors.GRAY_350}`,
      }}>
      <Editor
        height="100%"
        value={value}
        language={language}
        beforeMount={handleEditorBeforeMount}
        onMount={handleEditorDidMount}
        onChange={onValueChange}
        options={options}
      />
    </div>
  );
};
